/**
 * Row-keyed IndexedDB persistence for the entity store.
 *
 * One object store per collection, keyed by that collection's row key (composite keys included),
 * so a flush writes only the rows that changed. The layer this replaced put one array per
 * collection, which made a single inbound message re-serialize every row of the collection on the
 * next flush — a main-thread structured clone that grew with the workspace rather than with the
 * change (~116ms per flush on a 50k-row `item` collection, linear at ~2.3µs/row).
 *
 * Reads stay a bulk `getAll` per collection so boot is still one pass per collection, not a
 * row-by-row cursor walk.
 *
 * The write and read entry points are shaped after TanStack DB's `PersistenceAdapter`
 * (`loadSubset`, `applyCommittedTx`, and a monotonic row version) so the coordinator-backed
 * adapter in WEB-2236 can adopt this store rather than replace it. What it deliberately does not
 * have yet: the tombstone log and `pullSince` that let a follower tab catch up on missed
 * broadcasts, and the per-row `rowVersion` stamp and index those need. Nothing here reads across
 * tabs, so they would be unused weight today — and stamping every stored row measured a ~45%
 * regression on the boot restore this layer exists to keep fast (2.2s to 3.2s over a 300k-row
 * workspace), because it wraps every row in a second object for the structured clone to copy.
 * `rowVersion` is therefore kept once per flush, as the stream position; WEB-2236 adds the per-row
 * stamp and its index alongside the tombstone store, in the one schema change that gives all three
 * a caller.
 */

import { awaitOpen, type DeleteOutcome, deleteDatabaseBounded } from "@/store/persistence/idbConnection";

const META_STORE = "__meta__";
const META_KEY = "meta";

/** Rows to write under their keys. `key` is the collection's row key, computed by the caller. */
export type PersistedUpsert<T> = { key: string; row: T };

/** One committed transaction's effect on one collection. */
export type PersistedTx<T> = {
  upserts?: readonly PersistedUpsert<T>[];
  deletes?: readonly string[];
  /** Drops every row in the collection before applying `upserts` (a `clear()`). */
  truncate?: boolean;
};

export type PersistedMeta = {
  formatVersion: number;
  savedAt: string;
  /** Caller-provided cursor state captured atomically with the rows (bootstrap timestamps). */
  appMeta: unknown;
  /**
   * Monotonic across flushes — the stream position of the last write, which is what a follower
   * would ask to catch up from. Advances once per transaction, never resets.
   */
  rowVersion: number;
};

export type LoadedSubset<T> = { rows: T[]; latestRowVersion: number };

export type AppliedTxs = {
  rowsWritten: number;
  rowsDeleted: number;
  collections: number;
  /** IndexedDB transactions used; more than one means the write was chunked. */
  chunks: number;
};

function requestToPromise<T>(request: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error ?? new Error("IndexedDB request failed"));
  });
}

function openAt(dbName: string, version: number | undefined, create: readonly string[]): Promise<IDBDatabase> {
  return awaitOpen(version === undefined ? indexedDB.open(dbName) : indexedDB.open(dbName, version), dbName, (db) => {
    for (const name of create) {
      if (!db.objectStoreNames.contains(name)) db.createObjectStore(name);
    }
  });
}

/**
 * Opens the database with an object store for every collection. Missing stores are added by
 * reopening one version higher, so adding a collection to the schema does not need a format-version
 * bump — a new collection simply gets its store on the next boot.
 */
async function openRowStore(dbName: string, collections: readonly string[]): Promise<IDBDatabase> {
  const wanted = [META_STORE, ...collections];
  let db = await openAt(dbName, undefined, wanted);
  const missing = wanted.filter((name) => !db.objectStoreNames.contains(name));
  if (missing.length === 0) return db;
  const nextVersion = db.version + 1;
  db.close();
  db = await openAt(dbName, nextVersion, missing);
  return db;
}

/**
 * Row-keyed store for one snapshot scope. Instances are cheap: each call opens the database, does
 * its work, and closes, matching the previous snapshot layer's connection lifetime (a persist every
 * 5-30s, a restore once per boot) and keeping no handle open across a `deleteDatabase`.
 */
export class IndexedDbRowStore {
  public constructor(
    private readonly dbName: string,
    private readonly collections: readonly string[],
    private readonly formatVersion: number,
  ) {}

  /** The persisted meta record, or undefined when nothing has been written yet. */
  public async readMeta(): Promise<PersistedMeta | undefined> {
    const db = await openRowStore(this.dbName, this.collections);
    try {
      const tx = db.transaction(META_STORE, "readonly");
      return (await requestToPromise(tx.objectStore(META_STORE).get(META_KEY))) as PersistedMeta | undefined;
    } finally {
      db.close();
    }
  }

  /** The monotonic row version of the most recent flush; 0 when nothing has been written. */
  public async getStreamPosition(): Promise<number> {
    return (await this.readMeta())?.rowVersion ?? 0;
  }

  /**
   * Bulk-reads one collection. One `getAll` per collection — the whole point of keeping this on
   * IndexedDB rather than a row-by-row SQLite hydration.
   */
  public async loadSubset<T>(collectionId: string): Promise<LoadedSubset<T>> {
    const db = await openRowStore(this.dbName, this.collections);
    try {
      return await this.loadSubsetIn<T>(db, collectionId);
    } finally {
      db.close();
    }
  }

  /** Writes one collection's changed rows. Shaped after `PersistenceAdapter.applyCommittedTx`. */
  public async applyCommittedTx<T>(collectionId: string, tx: PersistedTx<T>, appMeta: unknown): Promise<AppliedTxs> {
    return this.applyCommittedTxs([new Map([[collectionId, tx]])], appMeta);
  }

  /**
   * Writes the given chunks of changed rows, one IndexedDB transaction each, and the meta record
   * with the last of them.
   *
   * Chunking bounds a single transaction: per-row keying makes the steady state cheap, but the
   * first flush after a cold bootstrap still has every row of the workspace to write, and issuing
   * 300k `put`s in one transaction blocks the main thread for the whole run. Awaiting between
   * chunks lets it breathe.
   *
   * The caller's cursor state lands only with the final chunk, so a tab killed part-way through
   * leaves rows without the cursors that would claim they are complete. The alternative — cursors
   * written with a half-applied row set — is the durable hole WEB-2236 exists to remove.
   *
   * Holding the previous cursors on the intermediate chunks is only safe while the write is purely
   * additive: extra rows under an older cursor are re-covered by the next delta sync. A truncate
   * breaks that, so from the chunk that applies one onward the intermediate cursors are null and a
   * crash mid-batch degrades to a cold bootstrap.
   */
  public async applyCommittedTxs(
    chunks: readonly ReadonlyMap<string, PersistedTx<unknown>>[],
    appMeta: unknown,
  ): Promise<AppliedTxs> {
    const written = { rowsWritten: 0, rowsDeleted: 0, collections: 0, chunks: 0 };
    const touched = new Set<string>();
    if (chunks.length === 0) return { ...written, collections: 0 };
    const db = await openRowStore(this.dbName, this.collections);
    try {
      const previous = (await requestToPromise(
        db.transaction(META_STORE, "readonly").objectStore(META_STORE).get(META_KEY),
      )) as PersistedMeta | undefined;
      let rowVersion = previous?.rowVersion ?? 0;
      let truncated = false;

      for (const [index, chunk] of chunks.entries()) {
        const names = [...chunk.keys()];
        if (names.length === 0) continue;
        const isLast = index === chunks.length - 1;
        rowVersion++;
        const tx = db.transaction([META_STORE, ...names], "readwrite");
        for (const name of names) {
          const change = chunk.get(name);
          if (!change) continue;
          touched.add(name);
          const objectStore = tx.objectStore(name);
          if (change.truncate) {
            objectStore.clear();
            truncated = true;
          }
          for (const key of change.deletes ?? []) {
            objectStore.delete(key);
            written.rowsDeleted++;
          }
          for (const { key, row } of change.upserts ?? []) {
            objectStore.put(row, key);
            written.rowsWritten++;
          }
        }
        const meta: PersistedMeta = {
          formatVersion: this.formatVersion,
          savedAt: new Date().toISOString(),
          // Not the caller's cursors until the whole write has landed; see the doc comment. Once a
          // truncate has been applied the previous cursors are worse than stale — they claim rows
          // the clear just removed — so they are dropped rather than carried, which costs a full
          // bootstrap on the next boot instead of leaving a hole no delta sync would refill.
          appMeta: isLast ? appMeta : truncated ? null : (previous?.appMeta ?? null),
          rowVersion,
        };
        tx.objectStore(META_STORE).put(meta, META_KEY);
        await new Promise<void>((resolve, reject) => {
          tx.oncomplete = () => resolve();
          tx.onerror = () => reject(tx.error ?? new Error("IndexedDB transaction failed"));
          tx.onabort = () => reject(tx.error ?? new Error("IndexedDB transaction aborted"));
        });
        written.chunks++;
      }
      return { ...written, collections: touched.size };
    } finally {
      db.close();
    }
  }

  /**
   * Reads the meta record plus every collection's rows. One transaction over every store, so a
   * concurrent flush can't interleave and hand boot a half-written view.
   */
  public async loadAll(): Promise<{ meta: PersistedMeta | undefined; data: Map<string, unknown[]> }> {
    const db = await openRowStore(this.dbName, this.collections);
    try {
      const tx = db.transaction([META_STORE, ...this.collections], "readonly");
      // Every request is issued before the first await: a read transaction commits as soon as it
      // goes idle, so awaiting one result before asking for the next risks the rest landing on a
      // finished transaction — and it serializes reads that IndexedDB would otherwise pipeline.
      const metaRequest = requestToPromise(tx.objectStore(META_STORE).get(META_KEY));
      const rowRequests = this.collections.map(
        (name) => [name, requestToPromise(tx.objectStore(name).getAll())] as const,
      );
      const meta = (await metaRequest) as PersistedMeta | undefined;
      const data = new Map<string, unknown[]>();
      for (const [name, request] of rowRequests) {
        const rows = (await request) as unknown[];
        if (rows.length > 0) data.set(name, rows);
      }
      return meta ? { meta, data } : { meta: undefined, data: new Map() };
    } finally {
      db.close();
    }
  }

  private async loadSubsetIn<T>(db: IDBDatabase, collectionId: string): Promise<LoadedSubset<T>> {
    const tx = db.transaction([META_STORE, collectionId], "readonly");
    const metaRequest = requestToPromise(tx.objectStore(META_STORE).get(META_KEY));
    const rowsRequest = requestToPromise(tx.objectStore(collectionId).getAll());
    const meta = (await metaRequest) as PersistedMeta | undefined;
    return { rows: (await rowsRequest) as T[], latestRowVersion: meta?.rowVersion ?? 0 };
  }
}

/**
 * Deletes the whole database for a scope, reporting whether another tab's connection is still
 * holding the delete open so the caller can tell the user rather than boot on regardless.
 */
export function deleteRowStore(dbName: string): Promise<DeleteOutcome> {
  return deleteDatabaseBounded(dbName);
}
